import React, { FC, useState } from 'react';

import { Modal, ModalProps } from '@opendoor/bricks/complex';
import { Button } from '@opendoor/bricks/core';
import { Layout, LayoutContent } from 'platform/components/layout/Layout';
import { InnerHeader } from 'platform/components/layout/InnerHeader';
import { useObservability } from 'shared/helpers/observability';

const CONFIRM_CLOSE_TEXT = 'Close and don’t save';
const CANCEL_CLOSE_TEXT = 'Cancel';

export interface StepComponentProps {
  /** Function to advance to the next step in the wizard */
  goToNextStep: () => void;
  /** Function to advance to the previous step in the wizard */
  goToPreviousStep: () => void;
  /** Function to close the wizard. This is equivalent to the user clicking the Close button. */
  closeWizard: () => void;
}

export interface StepInfo {
  analyticsName?: string;
  /** Function to render the step */
  content: (props: StepComponentProps) => JSX.Element;
  /** Optional title to display in the Wizard for this step */
  title?: string;
  /** Optional boolean to check if previous button should be rendered in wizard */
  hideGoBack?: boolean;
  /** Optional react node to render in the bottom of the page */
  stickyBottomContent?: (props: StepComponentProps) => JSX.Element;
  /** Optional object to require user to confirm closing the wizard */
  confirmOnClose?: {
    /** Optional text to show in the cancel button */
    cancelText?: string;
    /** Optional text to show in the confirm button */
    confirmText?: string;
    /** Content to render in the confirm close modal */
    content: JSX.Element;
    /** Optional set the size for the confirm modal on mobile */
    desktopAndTabletSize?: ModalProps['desktopAndTabletSize'];
    /** Optional set the size for the confirm modal on tablet and desktop */
    mobileSize?: ModalProps['mobileSize'];
    /** Title to display in the confirm on close modal */
    title: string;
  };
}

export interface WizardProps {
  /** Optional number of the step to show when the wizard first renders (first step is 1) */
  initialStepNumber?: number;
  /** Shows a loading animation. Useful while loading data to generate steps */
  isLoading?: boolean;
  /** Page to navigate to on close */
  steps: StepInfo[];
  /** Title to show in the header (But titles set in the StepInfo take precedence) */
  title: string;
  /** Optional boolean to hide the footer in the Layout */
  hideFooter?: boolean;
  /** Callback when the wizard is closed */
  onClose: () => void;
  /** Close button props  */
  closeButtonProps?: CloseButtonProps;
}

export interface CloseButtonProps {
  analyticsName: string;
  label: string;
  onClick: () => void;
  isVisible: boolean;
  name?: string;
  nameOverride?: (currentStepIndex: number) => string;
}

/** Reusable wizard component */
export const Wizard: FC<WizardProps> = ({
  initialStepNumber,
  isLoading,
  steps,
  title,
  hideFooter,
  closeButtonProps,
  onClose,
}) => {
  const { trackEvent } = useObservability();
  const [isConfirmOnCloseModalOpen, setIsConfirmOnCloseModalOpen] = useState(false);
  const totalSteps = steps.length;
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    // if there's an initialStepNumber, and it is within the step list bounds, use it as the initial step
    if (initialStepNumber && initialStepNumber <= totalSteps && initialStepNumber > 0) {
      return initialStepNumber - 1; // convert to index
    }
    return 0;
  });
  // Can be undefined if the consumer passes an empty list for steps (e.g. they're loading data)
  const currentStep: StepInfo | undefined = steps[currentStepIndex];

  /** Closes the wizard and resets the wizard state */
  const closeWizard = () => {
    setIsConfirmOnCloseModalOpen(false);
    onClose();
  };

  /** Handles what happens when the user clicks the close button */
  const onClickClose = () => {
    trackEvent(
      'click',
      `wizard-close${currentStep.analyticsName ? `-${currentStep.analyticsName}` : ''}`,
    );
    currentStep?.confirmOnClose ? setIsConfirmOnCloseModalOpen(true) : closeWizard();
  };

  /** Closes the confirmation modal  */
  const cancelClosingWizard = () => setIsConfirmOnCloseModalOpen(false);

  /** Handles navigating to the previous step */
  const goToPreviousStep = () => {
    scrollToTop();
    setCurrentStepIndex((currentStepIndex) => currentStepIndex - 1);
  };

  /** Advances the counter to go to the next step. If on the last step, do nothing */
  const goToNextStep = () => {
    scrollToTop();
    setCurrentStepIndex((currentStepIndex) =>
      currentStepIndex === totalSteps - 1 ? currentStepIndex : currentStepIndex + 1,
    );
  };
  const scrollToTop = () => window.scrollTo(0, 0);

  const props = closeButtonProps
    ? {
        rightComponent: (
          <Button
            analyticsName={closeButtonProps.analyticsName}
            aria-label={closeButtonProps.label}
            onClick={closeButtonProps.onClick}
            visibility={closeButtonProps.isVisible ? 'visible' : 'hidden'}
            variant="link"
            whiteSpace="nowrap"
          >
            {closeButtonProps.nameOverride
              ? closeButtonProps.nameOverride(currentStepIndex)
              : closeButtonProps.name}
          </Button>
        ),
      }
    : { onClose: onClickClose };

  return (
    <Layout
      data-testid="hub-wizard"
      hideNav
      hideFooter={hideFooter}
      isLoading={isLoading}
      renderHeader={() => (
        <InnerHeader
          title={currentStep?.title || title}
          onBack={currentStepIndex === 0 || currentStep?.hideGoBack ? undefined : goToPreviousStep}
          {...props}
          pageName=""
          progressBarProps={
            totalSteps > 1
              ? {
                  currentStep: currentStepIndex + 1,
                  totalSteps,
                }
              : undefined
          }
        />
      )}
      stickyBottomContent={
        (currentStep?.stickyBottomContent &&
          currentStep.stickyBottomContent({ goToNextStep, goToPreviousStep, closeWizard })) ||
        null
      }
    >
      <LayoutContent p="0" maxWidth="100%">
        {currentStep && (
          <>
            {/* Wizard content */}
            {currentStep.content({ goToNextStep, goToPreviousStep, closeWizard })}

            {/* Confirmation Modal */}
            {currentStep.confirmOnClose && (
              <Modal
                analyticsName={`cake-${currentStep.analyticsName ?? ''}-wizard-modal`}
                isOpen={isConfirmOnCloseModalOpen}
                onRequestClose={cancelClosingWizard}
                headerProps={{ title: currentStep.confirmOnClose.title, titleProps: { as: 'h2' } }}
                mobileSize={currentStep.confirmOnClose?.mobileSize}
                desktopAndTabletSize={currentStep.confirmOnClose?.desktopAndTabletSize}
              >
                {currentStep.confirmOnClose.content}

                <Button
                  analyticsName={`wizard-confirm-close${
                    currentStep.analyticsName ? `-${currentStep.analyticsName}` : ''
                  }`}
                  variant="secondary"
                  onClick={closeWizard}
                  mt="3"
                  aria-label={null}
                >
                  {currentStep.confirmOnClose.confirmText || CONFIRM_CLOSE_TEXT}
                </Button>
                <Button
                  analyticsName={`wizard-cancel-close${
                    currentStep.analyticsName ? `-${currentStep.analyticsName}` : ''
                  }`}
                  onClick={cancelClosingWizard}
                  mt="3"
                  aria-label={null}
                >
                  {currentStep.confirmOnClose.cancelText || CANCEL_CLOSE_TEXT}
                </Button>
              </Modal>
            )}
          </>
        )}
      </LayoutContent>
    </Layout>
  );
};
